import type { ReactNode } from 'react';

import { useLoaderData, useRouteLoaderData } from '@remix-run/react';
import type { TSurveyElement } from '@shared/dream-components';
import { SurveyElement } from '@shared/dream-components';

import useEmail from '~/hooks/useEmail';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { loader, RootLoaderData } from '~/root';

import SurveyProvider from './SurveyProvider';

interface Props {
  node: TSurveyElement;
  children: ReactNode | ReactNode[];
}

const editNode = (node: TSurveyElement) => {
  return {
    ...node,
    attrs: {
      ...node.attrs,
      isSurveyPageForm: true,
    },
  };
};

const Survey = ({ node, children }: Props) => {
  const loaderData = useLoaderData<typeof loader>();
  const publicationId = loaderData?.publication?.id;
  const nodeToRender = editNode(node);
  const [storedEmail] = useEmail();
  const { subscription } = useRouteLoaderData<RootLoaderData>('root') || {};
  const { nextPage } = useSignupRedirects();

  const onContinue = () => {
    nextPage();
  };

  const email = subscription?.email || storedEmail;

  return (
    <SurveyProvider
      hasEmail={!!email}
      email={email}
      id={nodeToRender?.attrs?.data?.id}
      publicationId={publicationId}
      successMessage={nodeToRender?.attrs?.data?.thank_you_message}
    >
      {({ isLoading, isSuccess, submittingText }) => (
        <SurveyElement
          element={nodeToRender}
          isSubmitting={isLoading}
          isSuccess={isSuccess}
          isLive
          hasLocalStorageEmail={!!email}
          onContinue={onContinue}
          submittingText={submittingText}
        >
          {children}
        </SurveyElement>
      )}
    </SurveyProvider>
  );
};

export default Survey;
